import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ClassifierDto } from "../../models/ClassifierDto";
import StatusBadge from "../StatusBadge";
import parse from "html-react-parser";
import KlasisList, { IKlasisListRow } from "../KlasisList";
import { format } from "date-fns";
import { Translations } from "../../localization/translations";
import FileList from "../../components/FileList";
import { ApiRoutes } from "../../configuration/apiRoutes";
import { useCallback } from "react";
import { ClassifierFileDto } from "../../models/ClassifierFileDto";
import axios from "axios";
import fileDownload from "js-file-download";

interface IClassifierPassportDataProps {
    data?: ClassifierDto;
}

const ClassifierPassportData: React.FC<IClassifierPassportDataProps> = (
    props: IClassifierPassportDataProps
) => {
    const { t } = useTranslation();

    const renderContactPersonData = ():
        | JSX.Element
        | JSX.Element[]
        | undefined => {
        if (
            !props.data?.contactPersons?.some(
                (cp) => cp.email || cp.fullName || cp.phone || cp.position
            )
        )
            return undefined;

        return props.data?.contactPersons?.map((item, index) => (
            <Box key={`contact-p-${index}`}>
                <Box>
                    <Typography>
                        <b>{item.fullName}</b>
                    </Typography>
                </Box>
                {item.position && (
                    <Box>
                        <Typography>{item.position}</Typography>
                    </Box>
                )}
                {item.phone && (
                    <Box>
                        <Typography>{item.phone}</Typography>
                    </Box>
                )}
                {item.email && (
                    <Box>
                        <Typography>{item.email}</Typography>
                    </Box>
                )}
            </Box>
        ));
    };

    const handleFileDownload = useCallback((file: ClassifierFileDto) => {
        const url = ApiRoutes.Classifiers.GetFile(file.id);

        axios.get(url, { responseType: "blob" }).then((response) => {
            if (!response) return null;

            fileDownload(
                response.data,
                `${file.fileName}${file.fileExtension}`
            );
        });
    }, []);

    const gridRows: IKlasisListRow[] = [
        {
            cols: [
                {
                    title: t(Translations.classifier.passportItem.abbreviation),
                    children: props.data?.abbreviation,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(
                        Translations.classifier.passportItem.mainAuthority
                    ),
                    children: props.data?.mainAuthority,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(
                        Translations.classifier.passportItem
                            .coResponsibleAuthority
                    ),
                    children: props.data?.coResponsibleAuthority,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(Translations.classifier.passportItem.status),
                    children: props.data?.status ? (
                        <StatusBadge label={props.data?.status} />
                    ) : undefined,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(
                        Translations.classifier.passportItem.publicationYear
                    ),
                    children: props.data?.publicationYear
                        ? props.data?.publicationYear?.toString()
                        : undefined,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(
                        Translations.classifier.passportItem
                            .classificationObject
                    ),
                    children: props.data?.classificationObject
                        ? parse(props.data?.classificationObject)
                        : undefined,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(Translations.classifier.passportItem.purpose),
                    children: props.data?.purpose
                        ? parse(props.data?.purpose)
                        : undefined,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(Translations.classifier.passportItem.area),
                    children: props.data?.area
                        ? parse(props.data?.area)
                        : undefined,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(Translations.classifier.passportItem.legalBasis),
                    children: props.data?.legalBasis
                        ? parse(props.data?.legalBasis)
                        : undefined,
                },
                {
                    title: t(Translations.classifier.passportItem.author),
                    children: props.data?.author
                        ? parse(props.data?.author)
                        : undefined,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(
                        Translations.classifier.passportItem.plannedRevision
                    ),
                    children: props.data?.plannedRevision
                        ? parse(props.data?.plannedRevision)
                        : undefined,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(Translations.classifier.passportItem.codingSystem),
                    children: props.data?.codingSystem
                        ? parse(props.data?.codingSystem)
                        : undefined,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(Translations.classifier.passportItem.availability),
                    children: props.data?.availability
                        ? parse(props.data?.availability)
                        : undefined,
                },
            ],
        },
        {
            cols: props.data?.extraFields.map(field => {
                return {
                    title: field.fieldKeyName,
                    children: parse(field.fieldContent)
                }
            }) ?? []
        },
        {
            cols: [
                {
                    title: t(Translations.classifier.label.relatedFiles),
                    children:
                        props.data?.files && props.data?.files.length > 0 ? (
                            <FileList
                                title={""}
                                files={props.data?.files}
                                onDownaloadClick={handleFileDownload}
                                showFileDate={true}
                            />
                        ) : undefined,
                },
            ],
        },
        {
            cols: [
                {
                    title: t(
                        Translations.classifier.passportItem.contactPerson
                    ),
                    children: renderContactPersonData(),
                },
            ],
        },
    ];

    return (
        <>
            <Grid container>
                <Grid item sx={{ marginLeft: "1rem" }}>
                    {props.data?.publicatedAt ? (
                        <Typography variant="caption" color="text.secondary">
                            {`${t(
                                Translations.classifier.passportItem.publicated
                            )}: ${format(
                                new Date(props.data?.publicatedAt),
                                t(Translations.formats.date.text)
                            )}`}
                        </Typography>
                    ) : null}
                </Grid>
            </Grid>
            <KlasisList rows={gridRows} />
        </>
    );
};

export default ClassifierPassportData;